import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  fetchConditionWorkspace,
  type ConditionWorkspaceResponse,
} from '../../domain/continuousSafety/conditionWorkspace';

type ConditionWorkspacePanelProps = {
  siteId: string;
};

const ConditionWorkspacePanel: React.FC<ConditionWorkspacePanelProps> = ({ siteId }) => {
  const [workspace, setWorkspace] = useState<ConditionWorkspaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setDisabled(false);
    setError(null);
    fetchConditionWorkspace({ siteId })
      .then((result) => {
        if (cancelled) return;
        if (result.status === 'disabled') {
          setDisabled(true);
          setWorkspace(null);
        } else if (result.status === 'error') {
          setError(result.message);
          setWorkspace(null);
        } else {
          setWorkspace(result.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWorkspace(null);
          setError('Condition workspace is temporarily unavailable.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading condition workspace…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (disabled) {
    return null;
  }

  if (error || !workspace) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }} data-testid="condition-workspace-panel">
        <Alert severity="warning" role="alert">
          {error || 'Condition workspace is unavailable.'} No work or attention conclusion is shown.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      data-testid="condition-workspace-panel"
      sx={{ p: 2 }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
        Condition workspace
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        Governed read model across conditions, work, scans, records, and attention.
        As of {workspace.queryAsOf}.
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
        <Chip size="small" label={`Conditions ${workspace.counts.conditions}`} />
        <Chip size="small" label={`Work ${workspace.counts.work}`} />
        <Chip size="small" label={`Scans ${workspace.counts.scans}`} />
        <Chip size="small" label={`Records ${workspace.counts.records}`} />
        <Chip
          size="small"
          color={workspace.counts.attention > 0 ? 'warning' : 'default'}
          label={`Attention ${workspace.counts.attention}`}
        />
      </Stack>
      {workspace.attention.length > 0 ? (
        <Alert severity="warning" sx={{ mt: 1.25 }} data-testid="condition-workspace-attention">
          {workspace.attention.length} attention item
          {workspace.attention.length === 1 ? '' : 's'} require review.
        </Alert>
      ) : (
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="body2" color="text.secondary">
            No attention items in the current workspace snapshot.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ConditionWorkspacePanel;
